import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { TokenKey } from 'src/shared/constants';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class JwtService {
  allowedPortals$ = new BehaviorSubject<string[]>([]);

  constructor(private readonly localStorageService: LocalStorageService) {
    this.updateAllowedPortals();
  }

  setToken(token: string) {
    if (token) {
      this.localStorageService.set(TokenKey, token);

      this.updateAllowedPortals();
    }
  }

  decodeToken(): Record<string, string> | undefined {
    const token = this.localStorageService.get(TokenKey);

    if (!token) {
      return;
    }

    return jwtDecode(token);
  }

  getToken(): string | null {
    return this.localStorageService.get(TokenKey);
  }

  removeToken(): void {
    this.localStorageService.remove(TokenKey);
  }

  isTokenExpired(): boolean {
    const token = this.decodeToken();

    if (!token) {
      return false;
    }

    const expiryTime: number = parseInt(token.exp);

    if (expiryTime) {
      return 1000 * expiryTime - new Date().getTime() < 5000;
    } else {
      return false;
    }
  }

  isTokenRenewable(): boolean {
    const token = this.decodeToken();

    if (!token) {
      return false;
    }

    const refreshExpiry: number = parseInt(token.RefreshExpiry);

    if (refreshExpiry) {
      return 1000 * refreshExpiry - new Date().getTime() > 5000;
    } else {
      return false;
    }
  }

  getUserId(): number | undefined {
    const token = this.decodeToken();

    if (!token?.sub) {
      return undefined;
    }

    return parseInt(token.sub);
  }

  getUserEmail(): string | undefined {
    const token = this.decodeToken();

    if (!token?.email) {
      return undefined;
    }

    return token.email;
  }

  getUserName(): string | undefined {
    const token = this.decodeToken();

    if (!token?.UserName) {
      return undefined;
    }

    return token.UserName;
  }

  getAuthMethod() {
    const token = this.decodeToken();

    if (!token?.authmethod) {
      return undefined;
    }

    return token.authmethod;
  }

  get currentEdOrgId(): number | undefined {
    const token = this.decodeToken();

    if (!token?.EdOrgId) {
      return undefined;
    }

    return parseInt(token.EdOrgId);
  }

  get currentEdOrgName(): string | undefined {
    const token = this.decodeToken();

    if (!token?.EdOrgName) {
      return undefined;
    }

    return token.EdOrgName;
  }

  get roles(): string[] {
    const token = this.decodeToken();

    if (!token?.role) {
      return [];
    }

    if (typeof token.role === 'string') {
      return [token.role];
    }

    if (Array.isArray(token.role)) {
      return token.role;
    }

    return [];
  }

  get allowedPortals(): string[] {
    const token = this.decodeToken();

    if (!token?.AllowedPortals) {
      return [];
    }

    if (typeof token.AllowedPortals === 'string') {
      return token.AllowedPortals.trim() ? [token.AllowedPortals] : [];
    }

    if (Array.isArray(token.AllowedPortals)) {
      return (token.AllowedPortals as string[]).filter((type: string) => type.trim() !== '');
    }

    return [];
  }

  updateAllowedPortals() {
    this.allowedPortals$.next(this.allowedPortals);
  }
}
